import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Switch, Table, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PermissionGroupDetailModal } from './PermissionGroupDetailModal';
import { PermissionGroupFormModal } from './PermissionGroupFormModal';
import {
  deletePlanFeatureGroup,
  featureNosFromDetail,
  getPlanFeatureGroupDetail,
  getPlanFeatureGroups,
  updatePlanFeatureGroup,
  type AdminPermissionGroup,
  type FeatureGroup,
  type FeatureGroupDetail
} from './permissionGroupManageData';

const GROUP_NO_PATTERN = /^[0-9]{21}$/;

function dash(value?: string) {
  return value ? value : '—';
}

export function PermissionGroupManage() {
  const [rows, setRows] = useState<FeatureGroup[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [draftName, setDraftName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingGroupNo, setEditingGroupNo] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<FeatureGroupDetail | null>(null);
  const [configHash, setConfigHash] = useState('');
  const [switchingNos, setSwitchingNos] = useState<string[]>([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewGroup, setViewGroup] = useState<AdminPermissionGroup | null>(null);
  const detailRequest = useRef(0);
  const switchingRef = useRef(new Set<string>());

  const applySearch = (name = draftName) => {
    setDisplayName(name.trim());
    setPageNum(1);
    setReloadKey((value) => value + 1);
  };

  const resetSearch = () => {
    setDraftName('');
    setDisplayName('');
    setPageNum(1);
    setReloadKey((value) => value + 1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPlanFeatureGroups({ pageNum, pageSize, displayName })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '权限组列表加载失败');
          setRows([]);
          setTotal(0);
          return;
        }
        const records = res.content?.records;
        const list = Array.isArray(records) ? records : [];
        const pageTotal = res.content?.total;
        if (list.length === 0 && pageNum > 1) {
          setPageNum(pageNum - 1);
          return;
        }
        setRows(list);
        setTotal(typeof pageTotal === 'number' ? pageTotal : list.length);
      })
      .catch(() => {
        if (!cancelled) message.error('权限组列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageNum, pageSize, displayName, reloadKey]);

  const closeForm = () => {
    detailRequest.current += 1;
    setFormOpen(false);
    setEditingGroupNo('');
    setDetail(null);
    setConfigHash('');
    setDetailLoading(false);
  };

  const openCreate = () => {
    detailRequest.current += 1;
    setEditingGroupNo('');
    setDetail(null);
    setConfigHash('');
    setDetailLoading(false);
    setFormOpen(true);
  };

  const openEdit = (row: FeatureGroup) => {
    const groupNo = row.groupNo?.trim() ?? '';
    if (!GROUP_NO_PATTERN.test(groupNo)) {
      message.warning('功能组编码须为 21 位数字');
      return;
    }
    const ticket = detailRequest.current + 1;
    detailRequest.current = ticket;
    setEditingGroupNo(groupNo);
    setDetail(null);
    setConfigHash('');
    setFormOpen(true);
    setDetailLoading(true);
    getPlanFeatureGroupDetail(groupNo)
      .then((res) => {
        if (detailRequest.current !== ticket) return;
        if (res.code !== 200 || !res.content?.data) {
          if (res.code !== -1) message.error(res.message || '权限组详情加载失败');
          setFormOpen(false);
          return;
        }
        setDetail(res.content.data);
        setConfigHash(typeof res.content.configHash === 'string' ? res.content.configHash : '');
      })
      .catch(() => {
        if (detailRequest.current !== ticket) return;
        message.error('权限组详情加载失败');
        setFormOpen(false);
      })
      .finally(() => {
        if (detailRequest.current === ticket) setDetailLoading(false);
      });
  };

  const toggleStatus = async (row: FeatureGroup, checked: boolean) => {
    const groupNo = row.groupNo?.trim() ?? '';
    if (!GROUP_NO_PATTERN.test(groupNo)) {
      message.warning('功能组编码须为 21 位数字');
      return;
    }
    if (switchingRef.current.has(groupNo)) return;
    switchingRef.current.add(groupNo);
    setSwitchingNos((prev) => [...prev, groupNo]);
    const status = checked ? 'ACTIVE' : 'DISABLED';
    try {
      const detailRes = await getPlanFeatureGroupDetail(groupNo);
      if (detailRes.code !== 200 || !detailRes.content?.data) {
        if (detailRes.code !== -1) message.error(detailRes.message || '权限组详情加载失败');
        return;
      }
      const data = detailRes.content.data;
      const displayName = data.displayName?.trim() ?? '';
      const hash = typeof detailRes.content.configHash === 'string' ? detailRes.content.configHash : '';
      if (!displayName || displayName.length > 128) {
        message.warning('缺少展示名称，无法修改状态');
        return;
      }
      if (!hash) {
        message.warning('缺少配置校验值，请稍后重试');
        return;
      }
      const res = await updatePlanFeatureGroup(groupNo, {
        displayName,
        status,
        featureNos: featureNosFromDetail(data),
        expectedConfigHash: hash
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || '修改状态失败');
        return;
      }
      const nextStatus =
        res.content?.data?.status === 'ACTIVE' || res.content?.data?.status === 'DISABLED'
          ? res.content.data.status
          : status;
      setRows((prev) => prev.map((item) => (item.groupNo === groupNo ? { ...item, status: nextStatus } : item)));
      message.success(res.message || (checked ? '已启用' : '已停用'));
    } catch {
      message.error('修改状态失败');
    } finally {
      switchingRef.current.delete(groupNo);
      setSwitchingNos((prev) => prev.filter((item) => item !== groupNo));
    }
  };

  const openDetail = async (row: FeatureGroup) => {
    const groupNo = row.groupNo?.trim() ?? '';
    if (!GROUP_NO_PATTERN.test(groupNo)) {
      message.warning('功能组编码须为 21 位数字');
      return;
    }
    const res = await getPlanFeatureGroupDetail(groupNo);
    if (res.code !== 200 || !res.content?.data) {
      if (res.code !== -1) message.error(res.message || '权限组详情加载失败');
      return;
    }
    const data = res.content.data;
    const features = Array.isArray(data.features) ? data.features : [];
    setViewGroup({
      id: data.groupNo?.trim() || groupNo,
      name: data.displayName?.trim() || row.displayName?.trim() || groupNo,
      description: '',
      businessCode: data.groupCode?.trim() || row.groupCode?.trim() || '',
      enabled: data.status === 'ACTIVE',
      createdAt: '',
      tagLabel: '',
      permissions: features
        .map((item) => {
          const id = item.featureNo?.trim() ?? '';
          if (!GROUP_NO_PATTERN.test(id)) return null;
          return {
            id,
            name: item.displayName?.trim() || item.featureCode?.trim() || id,
            resourceValue: item.featureCode?.trim() || null,
            description: '',
            enabled: item.status !== 'DISABLED'
          };
        })
        .filter((item): item is NonNullable<typeof item> => item != null)
    });
    setViewOpen(true);
  };

  const confirmDelete = (row: FeatureGroup) => {
    const groupNo = row.groupNo?.trim() ?? '';
    if (!GROUP_NO_PATTERN.test(groupNo)) {
      message.warning('功能组编码须为 21 位数字');
      return;
    }
    const name = row.displayName?.trim() || row.groupCode?.trim() || groupNo;
    Modal.confirm({
      title: '确认删除',
      content: `确定删除权限组「${name}」吗？`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        const detailRes = await getPlanFeatureGroupDetail(groupNo);
        if (detailRes.code !== 200 || !detailRes.content?.data) {
          if (detailRes.code !== -1) message.error(detailRes.message || '权限组详情加载失败');
          return;
        }
        const hash = typeof detailRes.content.configHash === 'string' ? detailRes.content.configHash : '';
        if (!hash) {
          message.warning('缺少配置校验值，请稍后重试');
          return;
        }
        const res = await deletePlanFeatureGroup(groupNo, hash);
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '删除权限分组失败');
          return;
        }
        message.success(res.message || '已删除');
        if (viewGroup?.id === groupNo) {
          setViewOpen(false);
          setViewGroup(null);
        }
        setReloadKey((value) => value + 1);
      }
    });
  };

  const columns: ColumnsType<FeatureGroup> = [
    {
      title: '序号',
      width: 64,
      render: (_, __, index) => (pageNum - 1) * pageSize + index + 1
    },
    {
      title: '功能组编码',
      dataIndex: 'groupNo',
      width: 220,
      render: (value?: string) => <span className='admin-pkg-code-id'>{dash(value)}</span>
    },
    {
      title: '功能组标识',
      dataIndex: 'groupCode',
      width: 180,
      render: (value?: string) => <code className='admin-dict-code'>{dash(value)}</code>
    },
    {
      title: '功能组名称',
      dataIndex: 'displayName',
      width: 200,
      ellipsis: true,
      render: (value?: string) => <span className='admin-cell-primary'>{dash(value)}</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      align: 'center',
      render: (value: string | undefined, row) => {
        const groupNo = row.groupNo?.trim() ?? '';
        return (
          <Switch
            size='small'
            checked={value === 'ACTIVE'}
            loading={switchingNos.includes(groupNo)}
            onChange={(checked) => toggleStatus(row, checked)}
          />
        );
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, row) => (
        <div className='admin-row-actions'>
          <button type='button' className='admin-link-btn' onClick={() => openEdit(row)}>
            编辑
          </button>
          <button type='button' className='admin-link-btn' onClick={() => openDetail(row)}>
            详情
          </button>
          <button
            type='button'
            className='admin-link-btn admin-link-btn--danger'
            onClick={() => confirmDelete(row)}
          >
            删除
          </button>
        </div>
      )
    }
  ];

  return (
    <div className='admin-page-card'>
      <div className='admin-filter-bar admin-pkg-filter-bar'>
        <Input
          allowClear
          className='admin-filter-search'
          placeholder='请输入权限分组名称'
          value={draftName}
          maxLength={128}
          onChange={(e) => setDraftName(e.target.value)}
          onPressEnter={() => applySearch()}
        />
        <Button type='primary' className='admin-pkg-query-btn' onClick={() => applySearch()}>
          查询
        </Button>
        <Button onClick={resetSearch}>重置</Button>
      </div>
      <div className='mb-3'>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          className='admin-pkg-create-btn'
          onClick={openCreate}
        >
          新增
        </Button>
      </div>
      <Table<FeatureGroup>
        rowKey={(row, index) => row.groupNo || `${row.groupCode ?? ''}-${index}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        size='middle'
        className='admin-user-table'
        scroll={{ x: 1000 }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (count) => `共 ${count} 条`,
          onChange: (current, size) => {
            const nextSize = Math.min(100, Math.max(1, size));
            if (nextSize !== pageSize) {
              setPageNum(1);
              setPageSize(nextSize);
              return;
            }
            setPageNum(current);
          }
        }}
      />
      <PermissionGroupDetailModal
        open={viewOpen}
        group={viewGroup}
        onClose={() => {
          setViewOpen(false);
          setViewGroup(null);
        }}
        onSave={() => undefined}
      />
      <PermissionGroupFormModal
        open={formOpen}
        mode={editingGroupNo ? 'edit' : 'create'}
        loading={detailLoading}
        groupNo={editingGroupNo}
        detail={detail}
        configHash={configHash}
        onClose={closeForm}
        onCreated={() => {
          closeForm();
          if (pageNum !== 1) setPageNum(1);
          setReloadKey((value) => value + 1);
        }}
        onSaved={() => {
          closeForm();
          setReloadKey((value) => value + 1);
        }}
      />
    </div>
  );
}
